// Media Center — locale-aware content selector.
// Returns the fully-shaped content object (strings, timeline, quick
// facts, downloads, UI labels…) for the active locale. English is the
// source-of-truth; anything missing from the Arabic overrides falls
// back cleanly to the English value so the page never renders a raw
// translation key or a blank heading.
//
// CMS wiring (Slice 1F cutover — Feb 2026)
//   • If `/api/media?lang=<lang>` returns a healthy DB payload, its
//     values override the hardcoded constants per-key via the adapter.
//   • If the API is disabled (501), times out, or returns a malformed
//     body, the hook silently falls back to the hardcoded constants —
//     the page never blanks, layout never shifts.
//   • The first render always uses the hardcoded shape; once the CMS
//     payload resolves and passes the health check, a state update
//     swaps in the DB values without changing keys or types.
import { useMemo } from "react";
import { useLocale } from "@/lib/LocaleContext";
import { useSocialProfiles } from "@/lib/useSocialProfiles";
import {
  TIMELINE, TIMELINE_DISCLAIMER, ALONGSIDE, ALONGSIDE_DISCLAIMER,
  GENRES, RECOGNITION, PRESS_ARTICLES, DOWNLOADS, DOWNLOAD_USAGE_NOTICE,
  HERO_STATS, NUMBERS, PLATFORM_FEATURES,
  STATS_DISCLAIMER, QUICK_FACTS, BIO_PARAGRAPHS,
  VISION_HEADLINE, VISION_PARAGRAPHS,
  GALLERY, PLATFORMS, BOOKING_CHANNELS, BOOKING,
} from "@/lib/mediaData";
import {
  TIMELINE_AR, TIMELINE_DISCLAIMER_AR, ALONGSIDE_AR, ALONGSIDE_DISCLAIMER_AR,
  GENRES_AR, RECOGNITION_AR, PRESS_HEADLINE_AR, DOWNLOADS_AR,
  DOWNLOAD_USAGE_NOTICE_AR, HERO_STATS_AR, NUMBERS_AR, PLATFORM_FEATURES_AR,
  STATS_DISCLAIMER_AR,
  QUICK_FACTS_AR, BIO_PARAGRAPHS_AR, VISION_HEADLINE_AR,
  VISION_PARAGRAPHS_AR, UI_AR, VENUE_LOCATION_AR,
} from "@/lib/mediaDataAr";
import { useMediaCmsPayload } from "@/lib/useMediaCms";
import { adaptCmsPayload, isPayloadHealthy } from "@/lib/mediaCmsAdapter";

// English UI strings — the source-of-truth for JSX inline copy.
// Any key missing from `UI_AR` transparently falls back to this map.
const UI_EN = {
  siteName: "MEDIA",
  brandLine: "DJ SPY OFFICIAL · MEDIA CENTER",
  navLabels: {
    biography:   "Biography",
    career:      "Career",
    numbers:     "By The Numbers",
    presence:    "Presence",
    recognition: "Recognition",
    press:       "Press Coverage",
    gallery:     "Gallery",
    downloads:   "Downloads",
    bookings:    "Bookings",
  },
  languageSwitch: "العربية",
  ctaViewAssets:   "View Media Assets",
  ctaMediaContact: "Media Contact",
  sections: {
    s01_hero:        "// DJ SPY OFFICIAL · MEDIA CENTER",
    s02_bio_eyebrow: "// SECTION 02",
    s02_bio_title:   "Who Is DJ SPY",
    s03_timeline_eyebrow: "// SECTION 03",
    s03_timeline_title:   "Career Timeline",
    s03_timeline_lede:    "Twenty-plus years across three continents, seven countries, and thousands of nights behind the decks.",
    s04_numbers_eyebrow: "// SECTION 04",
    s04_numbers_title:   "By The Numbers",
    s05_presence_eyebrow: "// SECTION 05",
    s05_presence_title:   "International Presence",
    s05_presence_lede:    "Featured performances across the region and every major club circuit.",
    s06_alongside_eyebrow: "// SECTION 06",
    s06_alongside_title:   "Performed Alongside",
    s07_music_eyebrow: "// SECTION 07",
    s07_music_title:   "The Music",
    s07_music_lede:    "Original productions, exclusive DJ edits, and professional-grade tools built for working DJs.",
    s07_music_output_eyebrow: "// OUTPUT",
    s07_music_output_title:   "Original Productions · Exclusive Edits · Professional Tools",
    s07_music_output_copy:    "Every track on the DJ SPY catalogue is engineered to slot into a working DJ set — precise BPMs, extended intros and outros, clean tags, and DJ-friendly arrangements.",
    s08_vision_eyebrow: "// SECTION 08 · FEATURE",
    s09_platform_eyebrow: "// SECTION 09",
    s09_platform_title:   "DJSPYOfficial.com",
    s09_platform_lede_prefix: "The digital home for DJ-ready Arabic club music — live at ",
    s09_platform_features_eyebrow: "// PLATFORM FEATURES",
    s09_platform_features: [
      "Professional DJ Music Store",
      "Instant Downloads",
      "Exclusive Remixes",
      "Original Productions",
      "Membership",
      "Blog · SEO Articles",
      "Professional Music Library",
    ],
    s10_recognition_eyebrow: "// SECTION 10",
    s10_recognition_title:   "Recognition",
    s11_verified_eyebrow: "// SECTION 11",
    s11_verified_title:   "Verified Presence",
    s11_verified_lede:    "Official DJ SPY platforms — for journalists and verification teams.",
    s12_press_eyebrow: "// SECTION 12",
    s12_press_title:   "Press & Media Coverage",
    s12_press_lede:    "Selected articles and features across regional publications. Click through to read each piece on its original source.",
    s12_press_read: "Read article →",
    s13_gallery_eyebrow: "// SECTION 13",
    s13_gallery_title:   "Media Gallery",
    s13_gallery_lede:    "High-resolution imagery, cleared for editorial use with attribution.",
    s13_gallery_categories: {
      "Editorial Portraits": "Editorial Portraits",
      "Live Performances":   "Live Performances",
      "Behind The Booth":    "Behind The Booth",
      "Festival Moments":    "Festival Moments",
    },
    s14_downloads_eyebrow: "// SECTION 14",
    s14_downloads_title:   "Download Center",
    s14_downloads_lede:    "Everything a journalist, promoter, or festival buyer needs — one page, one click.",
    s15_news_eyebrow: "// SECTION 15",
    s15_news_title:   "Latest News",
    s15_news_lede:    "Fresh from the DJ SPY editorial desk.",
    s16_bookings_eyebrow: "// SECTION 16",
    s16_bookings_title:   "International Bookings",
    s16_bookings_lede:    "Available worldwide. For festival bookings, brand partnerships, and editorial press.",
  },
  labels: {
    quickFacts:      "// QUICK FACTS",
    featuredVenue:   "// FEATURED VENUE",
    comingSoon:      "Coming Soon",
    unavailable:     "// TEMPORARILY UNAVAILABLE",
    unavailableCopy: "Venue directory couldn't be reached right now.",
    noVenues:        "// NO VENUES CONFIGURED",
    noVenuesCopy:    "Featured venues will appear here once added.",
    directChannels:  "// DIRECT CHANNELS",
    email:           "Email",
    telegram:        "Telegram",
    instagram:       "Instagram",
    tiktok:          "TikTok",
    website:         "Website",
    output:          "// OUTPUT",
    platformFeatures:"// PLATFORM FEATURES",
  },
  booking: {
    thanks_eyebrow: "// REQUEST RECEIVED",
    thanks_title:   "Thank you.",
    thanks_copy:    "Thank you for contacting DJ SPY Management. Your booking request has been received. Our team will review the event details and respond using the contact information you provided.",
    ph_name:        "Full name *",
    ph_company:     "Company or organization",
    ph_email:       "Email address",
    ph_phone:       "Phone or WhatsApp",
    ph_date:        "Event date *",
    ph_city:        "Event city and country *",
    ph_venue:       "Venue",
    ph_event_type:  "Event type (festival, club, private, corporate…)",
    ph_attendance:  "Expected attendance",
    ph_duration:    "Requested performance duration",
    ph_budget:      "Budget or offer (optional)",
    ph_message:     "Message — tell us about your event, audience, format… *",
    consent:        "I agree to be contacted by DJ SPY Management about this booking request.",
    submit:         "Send booking request",
    submitting:     "Sending…",
    err_name:       "Please share your full name.",
    err_contact:    "We need at least an email or a phone number to reply.",
    err_email:      "That email address doesn't look valid.",
    err_date:       "Please share the event date (an approximate date is fine).",
    err_city:       "Please share the event city and country.",
    err_message:    "Please tell us a little about your event.",
    err_consent:    "Please confirm you agree to be contacted.",
    toast_ok:       "Booking request received.",
    toast_err:      "Could not send booking request.",
  },
  hero: {
    roles: ["International DJ", "Producer", "Entrepreneur"],
  },
  footerLine: "DJ SPY · Available Worldwide",
};

// Deep merge helper — English (base) never loses. `override` only fills in
// truthy values; empty strings and undefined pass through to `base`.
function deepPick(base, override) {
  if (override === undefined || override === null) return base;
  if (typeof override !== "object" || Array.isArray(override)) {
    return override;
  }
  const out = { ...base };
  for (const k of Object.keys(override)) {
    out[k] = deepPick(base?.[k], override[k]);
  }
  return out;
}

export function useMediaContent() {
  const { lang, isRTL } = useLocale();
  const cms = useMediaCmsPayload(lang);
  const socialFeed = useSocialProfiles();

  return useMemo(() => {
    const isAr = lang === "ar";

    // 1) Hardcoded per-locale content — the guaranteed fallback shape.
    const fallback = {
      TIMELINE:              isAr ? TIMELINE_AR              : TIMELINE,
      TIMELINE_DISCLAIMER:   isAr ? TIMELINE_DISCLAIMER_AR   : TIMELINE_DISCLAIMER,
      ALONGSIDE:             isAr ? ALONGSIDE_AR             : ALONGSIDE,
      ALONGSIDE_DISCLAIMER:  isAr ? ALONGSIDE_DISCLAIMER_AR  : ALONGSIDE_DISCLAIMER,
      GENRES:                isAr ? GENRES_AR                : GENRES,
      RECOGNITION:           isAr ? RECOGNITION_AR           : RECOGNITION,
      PRESS_ARTICLES:        isAr ? PRESS_ARTICLES.map((a) => ({
                                       ...a,
                                       headline: PRESS_HEADLINE_AR[a.url] || a.headline,
                                     }))
                                  : PRESS_ARTICLES,
      DOWNLOADS:             isAr ? DOWNLOADS.map((d) => ({
                                       ...d,
                                       label: DOWNLOADS_AR[d.key]?.label || d.label,
                                       note:  DOWNLOADS_AR[d.key]?.note  || d.note,
                                       en_only: !!DOWNLOADS_AR[d.key]?.en_only,
                                     }))
                                  : DOWNLOADS,
      DOWNLOAD_USAGE_NOTICE: isAr ? DOWNLOAD_USAGE_NOTICE_AR : DOWNLOAD_USAGE_NOTICE,
      HERO_STATS:            isAr ? HERO_STATS_AR            : HERO_STATS,
      NUMBERS:               isAr ? NUMBERS_AR               : NUMBERS,
      PLATFORM_FEATURES:     isAr ? PLATFORM_FEATURES_AR     : PLATFORM_FEATURES,
      STATS_DISCLAIMER:      isAr ? STATS_DISCLAIMER_AR      : STATS_DISCLAIMER,
      QUICK_FACTS:           isAr ? QUICK_FACTS_AR           : QUICK_FACTS,
      BIO_PARAGRAPHS:        isAr ? BIO_PARAGRAPHS_AR        : BIO_PARAGRAPHS,
      VISION_HEADLINE:       isAr ? VISION_HEADLINE_AR       : VISION_HEADLINE,
      VISION_PARAGRAPHS:     isAr ? VISION_PARAGRAPHS_AR     : VISION_PARAGRAPHS,
      GALLERY:               GALLERY,
      PLATFORMS:             PLATFORMS,
      BOOKING_CHANNELS:      BOOKING_CHANNELS,
      BOOKING:               BOOKING,
    };

    // 2) Overlay the CMS payload — but only if it's healthy enough to
    // trust. Any per-key adapter that returns undefined leaves the
    // hardcoded fallback in place, so a partial CMS payload never
    // blanks a section.
    let cmsOverride = null;
    let source = cms.source; // "loading" | "cms" | "fallback"
    if (cms.source === "cms" && isPayloadHealthy(cms.payload)) {
      cmsOverride = adaptCmsPayload(cms.payload);
    } else if (cms.source === "cms") {
      // Payload arrived but failed health check → downgrade.
      source = "fallback";
    }

    const content = cmsOverride ? { ...fallback, ...cmsOverride } : fallback;

    // 2c) Centralized social-profiles override — SINGLE SOURCE OF TRUTH
    // for DJ SPY's public profile links (music + social). This replaces
    // whatever `PLATFORMS` / `BOOKING_CHANNELS` came from the hardcoded
    // fallback OR the Media CMS `verified_presence` block, so every
    // Media page section reflects the current Admin → SOCIAL config.
    //
    // • Verified Presence (PLATFORMS): the Website entry stays first
    //   (it's the site itself, not a social profile), followed by every
    //   enabled music platform then every enabled social platform in
    //   Admin display order.
    // • Direct Channels (BOOKING_CHANNELS): social profiles only —
    //   these appear beside the booking form, where music-streaming
    //   URLs would be off-topic. Booking email/website live in
    //   `BOOKING` and remain untouched.
    // • When the public profile API is unavailable, we do NOT fall
    //   back to a stale hardcoded profile list — the Website entry is
    //   the only guaranteed row.  Per user directive: no outdated
    //   URLs may leak through.
    const websiteEntry = { name: "Website", handle: "djspyofficial.com", url: BOOKING.website, platform_slug: "website" };
    const socialFeedReady = !socialFeed.loading &&
      (socialFeed.music_platforms.length + socialFeed.social_media.length) > 0;
    if (socialFeedReady) {
      const musicRows = socialFeed.music_platforms.map((p) => ({
        name: p.display_name, handle: "", url: p.url, platform_slug: p.platform_slug, icon_hint: p.icon_hint,
      }));
      const socialRows = socialFeed.social_media.map((p) => ({
        name: p.display_name, handle: "", url: p.url, platform_slug: p.platform_slug, icon_hint: p.icon_hint,
      }));
      content.PLATFORMS = [websiteEntry, ...musicRows, ...socialRows];
      content.BOOKING_CHANNELS = socialRows;
    } else {
      // API loading OR empty — only the Website row survives. Do not
      // show stale hardcoded profile URLs.
      content.PLATFORMS = [websiteEntry];
      content.BOOKING_CHANNELS = [];
    }

    // 3) UI strings — deep-merge with EN fallback so any missing key
    // still renders correctly (never a translation-key placeholder).
    const ui = deepPick(UI_EN, isAr ? UI_AR : {});

    return {
      lang, isRTL, isAr,
      ui,
      venueLoc: isAr ? VENUE_LOCATION_AR : { countries: {}, cities: {} },
      // Diagnostic surface for tests + debug tools. Never read by the
      // 8 section components — the shape they consume is unchanged.
      cmsSource:      source,
      cmsError:       cms.error,
      cmsCacheVersion: cms.payload?.cache_version || null,
      ...content,
    };
  }, [lang, isRTL, cms.payload, cms.source, cms.error, socialFeed.loading, socialFeed.music_platforms, socialFeed.social_media]);
}
